import { DEVELOPER_ROLE, PERSONA_EMAIL_DOMAIN, SYNTHETIC_ROLE } from "@pax/api-contract/common";
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { UNREACHABLE_MESSAGE } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { clearAccountQueryData, type Session, sessionKey } from "./queries";

type ListUsersResult = Awaited<ReturnType<typeof authClient.admin.listUsers>>;
type ListedUser = NonNullable<ListUsersResult["data"]>["users"][number];

/** Someone a developer can act as. */
export interface ActAsTarget {
  id: string;
  name: string;
  email: string;
  isAnonymous: boolean;
}

const actAsKey = ["act-as"] as const;
const SEARCH_LIMIT = 8;
const FETCH_LIMIT = 20;

/** Only developers can act as other people. */
export function canActAs(session: Session | null | undefined): boolean {
  return session?.user.role === DEVELOPER_ROLE;
}

export function isActingAs(session: Session | null | undefined): boolean {
  return typeof session?.session.impersonatedBy === "string";
}

function toTarget(user: ListedUser): ActAsTarget {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    isAnonymous: user.isAnonymous === true,
  };
}

async function listUsers(query: {
  searchValue: string;
  searchField: "email" | "name";
  searchOperator: "contains" | "ends_with";
}): Promise<ActAsTarget[]> {
  const { data, error } = await authClient.admin.listUsers({
    query: { ...query, limit: FETCH_LIMIT, sortBy: "name", sortDirection: "asc" },
  });
  if (error) throw new Error(error.message?.trim() || UNREACHABLE_MESSAGE);
  // Better Auth refuses to act as a developer. Synthetic players belong to evals, so nobody acts as
  // them.
  return data.users
    .filter(user => user.role !== DEVELOPER_ROLE && user.role !== SYNTHETIC_ROLE)
    .map(toTarget);
}

export const personasQuery = queryOptions({
  queryKey: [...actAsKey, "personas"],
  queryFn: () =>
    listUsers({
      searchValue: `@${PERSONA_EMAIL_DOMAIN}`,
      searchField: "email",
      searchOperator: "ends_with",
    }),
  staleTime: 5 * 60_000,
});

export function userSearchQuery(term: string) {
  const trimmed = term.trim();
  return queryOptions({
    queryKey: [...actAsKey, "search", trimmed],
    queryFn: async () => {
      const [byEmail, byName] = await Promise.all([
        listUsers({
          searchValue: trimmed.toLowerCase(),
          searchField: "email",
          searchOperator: "contains",
        }),
        listUsers({ searchValue: trimmed, searchField: "name", searchOperator: "contains" }),
      ]);
      return [...new Map([...byEmail, ...byName].map(user => [user.id, user])).values()]
        .sort((left, right) => left.name.localeCompare(right.name))
        .slice(0, SEARCH_LIMIT);
    },
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

export async function actAs(queryClient: QueryClient, userId: string): Promise<void> {
  const { error } = await authClient.admin.impersonateUser({ userId });
  if (error) throw new Error(error.message?.trim() || UNREACHABLE_MESSAGE);
  await refreshAfterSessionChange(queryClient);
}

export async function stopActing(queryClient: QueryClient): Promise<void> {
  const { error } = await authClient.admin.stopImpersonating();
  if (error) throw new Error(error.message?.trim() || UNREACHABLE_MESSAGE);
  await refreshAfterSessionChange(queryClient);
}

async function refreshAfterSessionChange(queryClient: QueryClient): Promise<void> {
  clearAccountQueryData(queryClient);
  queryClient.removeQueries({ queryKey: actAsKey });
  await queryClient.resetQueries({ queryKey: sessionKey, exact: true });
}
